import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/api";
import { useLanguage, getTranslation, formatRenewalTime } from "../utils/language";
import { getDepartmentEmoji } from "../components/Popups";
import "../style/my-account.css";

// ─── Billing History Modal ────────────────────────────────────────────────────
function BillingModal({ onClose }) {
  const [txns, setTxns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [language]            = useLanguage();

  useEffect(() => {
    let mounted = true;
    const fetchBilling = async () => {
      try {
        const res = await API.get("/user/billing-history");
        if (!mounted) return;
        setTxns(res.data.transactions || []);
      } catch {
        if (!mounted) return;
        setError("Unable to load billing history.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchBilling();
    return () => { mounted = false; };
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });

  return (
    <div
      className="ma-modal-overlay"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="ma-modal">
        <div className="ma-modal-header">
          <h2 className="ma-modal-title">{getTranslation(language, "account", "billingHistory")}</h2>
          <button className="ma-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ma-modal-body">
          {loading && (
            <div className="ma-modal-center">
              <div className="ma-spinner" />
              <p>{getTranslation(language, "sidebar", "loading")}</p>
            </div>
          )}
          {!loading && error && (
            <div className="ma-modal-center">
              <p className="ma-modal-error">{error}</p>
            </div>
          )}
          {!loading && !error && txns.length === 0 && (
            <div className="ma-modal-empty">
              <p>No purchases yet.</p>
            </div>
          )}
          {!loading && !error && txns.length > 0 && (
            <div className="ma-txn-list">
              {txns.map((t) => (
                <div key={t._id} className="ma-txn-card">
                  <div className="ma-txn-icon" />
                  <div className="ma-txn-info">
                    <div className="ma-txn-plan">{t.plan?.name || "Plan Purchase"}</div>
                    {t.plan?.description && (
                      <div className="ma-txn-desc">{t.plan.description}</div>
                    )}
                    <div className="ma-txn-meta">
                      <span>{formatDate(t.date)}</span>
                      {t.paymentId && (
                        <span className="ma-txn-pid" title={t.paymentId}>
                          ID: {t.paymentId.slice(0, 18)}…
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ma-txn-amounts">
                    <span className="ma-txn-credits">+{t.amount} {getTranslation(language, "account", "credits").toLowerCase()}</span>
                    {t.plan?.price != null && (
                      <span className="ma-txn-price">₹{t.plan.price}</span>
                    )}
                    <span className="ma-txn-balance">bal: {t.balanceAfter}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main MyAccount Page ──────────────────────────────────────────────────────
export default function MyAccount() {
  const navigate = useNavigate();
  const [language] = useLanguage();

  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showBilling, setShowBilling] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const res = await API.get("/user/my-account");
        if (!mounted) return;
        setProfile(res.data);
      } catch (err) {
        console.error("Profile fetch failed:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchProfile();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="ma-loader-wrap">
        <div className="ma-spinner" />
        <p>{getTranslation(language, "account", "loadingProfile")}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="ma-loader-wrap">
        <p className="ma-error-text">{getTranslation(language, "account", "unableProfile")}</p>
      </div>
    );
  }

  const {
    name                 = "",
    email                = "",
    role                 = "user",
    profilePicture       = "",
    avatar               = "",
    credits              = 0,
    totalCreditsPurchased = 0,
    totalCreditsUsed     = 0,
    dailyCreditsUsed     = 0,
    dailyCreditsAllocated = 10,
    isBlocked            = false,
    departments          = [],
    createdAt,
    lastDailyCreditReset = null,
    nextResetAt          = null,
  } = profile;

  const initials = name
    ? name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const joinDate = new Date(createdAt).toLocaleDateString("en-IN", {
    year: "numeric", month: "long", day: "numeric",
  });

  const dailyAlloc = dailyCreditsAllocated || 10;
  const usedToday = dailyCreditsUsed > 0
    ? dailyCreditsUsed
    : (totalCreditsPurchased === 0 && totalCreditsUsed > 0 && credits === dailyAlloc
        ? Math.min(dailyAlloc, totalCreditsUsed)
        : Math.max(0, dailyAlloc - credits));

  const creditsLeft = totalCreditsPurchased > 0
    ? credits
    : (credits < dailyAlloc ? credits : Math.max(0, dailyAlloc - usedToday));

  const totalAllocated = totalCreditsPurchased > 0
    ? Math.max(totalCreditsPurchased, credits + totalCreditsUsed)
    : dailyAlloc;

  const currentUsed = totalCreditsPurchased > 0
    ? totalCreditsUsed
    : usedToday;

  const usedPct = totalAllocated > 0
    ? Math.min(100, Math.round((currentUsed / totalAllocated) * 100))
    : 0;

  return (
    <div className="ma-page">

      <div className="ma-orb ma-orb--1" />
      <div className="ma-orb ma-orb--2" />

      <div className="ma-wrap">

        <div className="ma-header-row">
          <div>
            <p className="ma-page-eyebrow">{getTranslation(language, "navbar", "brandSub")}</p>
            <h1 className="ma-page-title">{getTranslation(language, "account", "myAccount")}</h1>
          </div>
          <button
            className="ma-back-btn"
            onClick={() => navigate("/")}
            aria-label="Close and Go Home"
            type="button"
          >
            ✕
          </button>
        </div>

        {/* ── Profile ── */}
        <section className="ma-card ma-profile-card" style={{ "--delay": "0s" }}>
          <div className="ma-avatar-wrap">
            {profilePicture || avatar ? (
              <img
                src={profilePicture || avatar}
                alt={name}
                className="ma-avatar"
                style={{ objectFit: "cover" }}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="ma-avatar">{initials}</div>
            )}
            <div className="ma-avatar-glow" />
          </div>
          <div className="ma-profile-body">
            <h2 className="ma-profile-name">{name}</h2>
            <p className="ma-profile-email">{email}</p>
            <div className="ma-tags">
              <span className="ma-tag ma-tag--role">
                {role === "admin" ? getTranslation(language, "account", "admin") : getTranslation(language, "account", "user")}
              </span>
              <span className={`ma-tag ${isBlocked ? "ma-tag--danger" : "ma-tag--ok"}`}>
                {isBlocked ? getTranslation(language, "account", "blocked") : getTranslation(language, "account", "active")}
              </span>
              <span className="ma-tag ma-tag--date">
                {getTranslation(language, "account", "joinedOn")} {joinDate}
              </span>
              {!isBlocked && (
                <button
                  className="ma-edit-btn"
                  onClick={() => navigate("/edit-account")}
                >
                  {getTranslation(language, "account", "editProfile")}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ── Credits ── */}
        <section className="ma-card" style={{ "--delay": ".08s" }}>
          <h3 className="ma-card-title">{getTranslation(language, "account", "credits")}</h3>
          <div className="ma-cstats">
            <div className="ma-cstat">
              <span className="ma-cstat-num">{creditsLeft}</span>
              <span className="ma-cstat-lbl">{getTranslation(language, "account", "balance")}</span>
            </div>
            <div className="ma-cstat-sep" />
            <div className="ma-cstat">
              <span className="ma-cstat-num">{totalCreditsPurchased}</span>
              <span className="ma-cstat-lbl">{getTranslation(language, "account", "totalPurchased")}</span>
            </div>
            <div className="ma-cstat-sep" />
            <div className="ma-cstat">
              <span className="ma-cstat-num">{totalCreditsUsed}</span>
              <span className="ma-cstat-lbl">{getTranslation(language, "account", "used")}</span>
            </div>
          </div>
          <div className="ma-progress-block">
            <div className="ma-progress-top">
              <span>{getTranslation(language, "account", "creditUsage")}</span>
              <span className="ma-pct">{usedPct}%</span>
            </div>
            <div className="ma-track">
              <div className="ma-fill" style={{ width: `${usedPct}%` }} />
            </div>
          </div>
          {formatRenewalTime(nextResetAt, lastDailyCreditReset) && (
            <div className="ma-renewal-info">
              <span className="ma-renewal-icon">🔄</span>
              <span className="ma-renewal-text">
                {formatRenewalTime(nextResetAt, lastDailyCreditReset)}
              </span>
            </div>
          )}
        </section>

        {/* ── Department Preferences ── */}
        <section className="ma-card" style={{ "--delay": ".14s" }}>
          <h3 className="ma-card-title">{getTranslation(language, "account", "deptPref")}</h3>
          {departments.length === 0 ? (
            <div className="ma-dept-empty">
              <span>{getTranslation(language, "account", "noDept")}</span>
              {!isBlocked && (
                <button
                  className="ma-dept-add-btn"
                  onClick={() => navigate("/edit-account")}
                >
                  {getTranslation(language, "account", "selectDept")}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="ma-dept-chips">
                {departments.map((d) => {
                  const deptName = typeof d === "object" ? d.name : d;
                  const deptKey = (typeof d === "object" && (d._id || d.name)) ? (d._id || d.name) : d;
                  return (
                    <span key={deptKey} className="ma-dept-chip">
                      {getDepartmentEmoji(deptName)} {deptName}
                    </span>
                  );
                })}
              </div>
              {!isBlocked && (
                <button
                  className="ma-dept-edit-btn"
                  onClick={() => navigate("/edit-account")}
                >
                  {getTranslation(language, "account", "changeDept")}
                </button>
              )}
            </>
          )}
        </section>

        {/* ── Actions ── */}
        <section className="ma-card ma-actions-card" style={{ "--delay": ".2s" }}>
          <h3 className="ma-card-title">{getTranslation(language, "account", "actions")}</h3>
          <div className="ma-action-list">
            <button
              className="ma-action-row"
              onClick={() => setShowBilling(true)}
            >
              <span className="ma-action-icon" />
              <div className="ma-action-text">
                <span className="ma-action-label">{getTranslation(language, "account", "billingHistory")}</span>
                <span className="ma-action-sub">{getTranslation(language, "account", "billingDesc")}</span>
              </div>
              <span className="ma-action-chevron">›</span>
            </button>

            {!isBlocked && role !== "admin" && (
              <button
                className="ma-action-row ma-action-row--upgrade"
                onClick={() => navigate("/subscription")}
              >
                <span className="ma-action-icon" />
                <div className="ma-action-text">
                  <span className="ma-action-label">{getTranslation(language, "account", "upgradePlan")}</span>
                  <span className="ma-action-sub">{getTranslation(language, "account", "upgradeDesc")}</span>
                </div>
                <span className="ma-action-chevron">›</span>
              </button>
            )}
          </div>
        </section>

      </div>

      {showBilling && (
        <BillingModal onClose={() => setShowBilling(false)} />
      )}

    </div>
  );
}
